/**
 * VISTOERA_BILLING_PLAN_CREDITS_TOPUP_PAYG_REWARDS_AND_PROMOTIONS_
 * FOUNDATION_P0_V1 — "按量计费 / 积分充值" tab (task spec §12).
 *
 * Four independently-saved sections (task spec §18.1): PAYG Policy,
 * 积分充值包 (TopUpPackage), 自定义充值 (CustomTopUpPolicy per currency),
 * 充值赠送规则 (TopUpBonusRule, task spec §10).
 */
import { Alert, Button, Drawer, Form, Input, InputNumber, Select, Space, Switch, Table, Tag, Typography, message } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import {
  platformArchiveTopupPackage,
  platformCreateTopupBonusRule,
  platformCreateTopupPackage,
  platformDeleteTopupBonusRule,
  platformGetPaygPolicy,
  platformGetTopupPaygOverview,
  platformListCustomTopupPolicies,
  platformListTopupBonusRules,
  platformListTopupPackages,
  platformQuoteCustomTopup,
  platformUpdatePaygPolicy,
  platformUpdateTopupBonusRule,
  platformUpdateTopupPackage,
  platformUpsertCustomTopupPolicy,
} from '../../api'
import type {
  CustomTopUpPolicy,
  CustomTopUpQuote,
  PaygPolicy,
  TopUpBonusRule,
  TopUpPackage,
  TopupPaygOverview,
} from '../../types'
import { SectionCard, SummaryCard } from '../adminShared'
import { platformColors } from '../theme'
import {
  ACTIVE_CURRENCIES,
  errMsg,
  formatCredits,
  formatMoneyMinor,
  formatPricePerCredit,
  majorNumberToMinor,
  minorToMajorNumber,
} from './billingShared'

export default function TopupPaygTab() {
  const [overview, setOverview] = useState<TopupPaygOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [packages, setPackages] = useState<TopUpPackage[]>([])
  const [customPolicies, setCustomPolicies] = useState<CustomTopUpPolicy[]>([])
  const [bonusRules, setBonusRules] = useState<TopUpBonusRule[]>([])
  const [paygPolicy, setPaygPolicy] = useState<PaygPolicy | null>(null)

  const load = () => {
    setLoading(true)
    setError(null)
    Promise.all([
      platformGetTopupPaygOverview(),
      platformListTopupPackages(),
      platformListCustomTopupPolicies(),
      platformListTopupBonusRules(),
      platformGetPaygPolicy(),
    ])
      .then(([ov, pkgs, custom, bonus, payg]) => {
        setOverview(ov.data)
        setPackages(pkgs.data)
        setCustomPolicies(custom.data)
        setBonusRules(bonus.data)
        setPaygPolicy(payg.data)
      })
      .catch((err) => setError(errMsg(err, '加载按量计费/充值数据失败')))
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <SummaryCard title="启用充值包数量" value={overview?.enabled_package_count ?? '—'} iconBg={platformColors.primarySoft} icon="🎫" loading={loading} />
        <SummaryCard title="支持币种" value={overview?.supported_currencies?.join(' / ') || '—'} iconBg={platformColors.infoBg} icon="💱" loading={loading} />
        <SummaryCard
          title="PAYG 状态"
          value={overview?.payg_enabled ? '已启用' : '未启用'}
          iconBg={overview?.payg_enabled ? platformColors.successBg : platformColors.dangerBg}
          icon="📶"
          loading={loading}
        />
        <SummaryCard
          title="最低充值金额"
          value={overview?.min_topup_amount_minor != null ? formatMoneyMinor(overview.min_topup_amount_minor, overview.supported_currencies[0] || 'CNY') : '—'}
          iconBg={platformColors.warningBg}
          icon="💰"
          loading={loading}
        />
        <SummaryCard title="当前有效促销" value={overview?.active_promotion_count ?? '—'} iconBg={platformColors.primarySoft} icon="🏷️" loading={loading} />
      </Space>

      {error && <Alert type="error" message={error} showIcon style={{ margin: '4px 0 12px' }} />}

      <PaygPolicyCard policy={paygPolicy} loading={loading} onSaved={setPaygPolicy} />
      <TopUpPackagesCard packages={packages} loading={loading} onChanged={setPackages} />
      <CustomTopUpCard policies={customPolicies} loading={loading} onChanged={setCustomPolicies} />
      <TopUpBonusRulesCard rules={bonusRules} loading={loading} onChanged={setBonusRules} />
    </div>
  )
}

function PaygPolicyCard({
  policy,
  loading,
  onSaved,
}: {
  policy: PaygPolicy | null
  loading: boolean
  onSaved: (p: PaygPolicy) => void
}) {
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!policy) return
    form.setFieldsValue({
      enabled: policy.enabled,
      currency: policy.currency,
      price_per_credit: Number(policy.price_per_credit),
      minimum_billing_unit: Number(policy.minimum_billing_unit),
      settlement_threshold_minor: policy.settlement_threshold_minor != null ? minorToMajorNumber(policy.settlement_threshold_minor, policy.currency) : null,
      monthly_spending_cap_minor: policy.monthly_spending_cap_minor != null ? minorToMajorNumber(policy.monthly_spending_cap_minor, policy.currency) : null,
    })
  }, [policy, form])

  const save = async () => {
    const values = await form.validateFields()
    setSaving(true)
    try {
      const res = await platformUpdatePaygPolicy({
        enabled: values.enabled,
        currency: values.currency,
        price_per_credit: values.price_per_credit,
        minimum_billing_unit: values.minimum_billing_unit,
        settlement_threshold_minor:
          values.settlement_threshold_minor != null ? majorNumberToMinor(values.settlement_threshold_minor, values.currency) : null,
        monthly_spending_cap_minor:
          values.monthly_spending_cap_minor != null ? majorNumberToMinor(values.monthly_spending_cap_minor, values.currency) : null,
      })
      onSaved(res.data)
      message.success('按量计费策略已保存')
    } catch (err) {
      message.error(errMsg(err, '保存失败'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <SectionCard
      title="按量计费规则（Pay-as-you-go）"
      extra={
        <Button type="primary" size="small" loading={saving} onClick={() => void save()} disabled={loading}>
          保存
        </Button>
      }
    >
      <Typography.Paragraph type="secondary" style={{ margin: '0 4px 10px', fontSize: 12 }}>
        为套餐积分用尽（且套餐 exhaustion_policy = PAYG_ALLOWED）的租户设置按量计费单价与月度消费上限。这是「继续消费产生真实货币应付金额」的策略与负债基础，本轮不接入真实支付扣款（PAYMENT_CAPTURE_IMPLEMENTED=NO）。
      </Typography.Paragraph>
      {!loading && policy && (
        <Form form={form} layout="inline" style={{ padding: '0 4px' }}>
          <Form.Item name="enabled" label="启用 PAYG" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="currency" label="币种" rules={[{ required: true }]}>
            <Select style={{ width: 100 }} options={ACTIVE_CURRENCIES.map((c) => ({ label: c, value: c }))} />
          </Form.Item>
          <Form.Item name="price_per_credit" label="积分单价" rules={[{ required: true }]}>
            <InputNumber min={0} step={0.001} style={{ width: 110 }} addonAfter="/ 积分" />
          </Form.Item>
          <Form.Item name="minimum_billing_unit" label="最小计费单位" rules={[{ required: true }]}>
            <InputNumber min={0.01} step={0.01} style={{ width: 110 }} addonAfter="积分" />
          </Form.Item>
          <Form.Item name="settlement_threshold_minor" label="结算阈值">
            <InputNumber min={0} step={1} style={{ width: 120 }} placeholder="不限" />
          </Form.Item>
          <Form.Item name="monthly_spending_cap_minor" label="月度消费上限">
            <InputNumber min={0} step={1} style={{ width: 120 }} placeholder="不限" />
          </Form.Item>
        </Form>
      )}
    </SectionCard>
  )
}

function TopUpPackagesCard({
  packages,
  loading,
  onChanged,
}: {
  packages: TopUpPackage[]
  loading: boolean
  onChanged: (p: TopUpPackage[]) => void
}) {
  const [addOpen, setAddOpen] = useState(false)
  const [editing, setEditing] = useState<TopUpPackage | null>(null)

  const toggleEnabled = async (row: TopUpPackage, enabled: boolean) => {
    try {
      const res = await platformUpdateTopupPackage(row.id, { enabled })
      onChanged(packages.map((p) => (p.id === row.id ? res.data : p)))
    } catch (err) {
      message.error(errMsg(err, '更新失败'))
    }
  }

  const archive = async (row: TopUpPackage) => {
    try {
      await platformArchiveTopupPackage(row.id)
      onChanged(packages.map((p) => (p.id === row.id ? { ...p, enabled: false } : p)))
      message.success('已停用充值包（保留历史记录，非物理删除）')
    } catch (err) {
      message.error(errMsg(err, '停用失败'))
    }
  }

  return (
    <SectionCard
      title="积分充值包"
      extra={
        <Button type="primary" size="small" onClick={() => setAddOpen(true)}>
          + 新增充值包
        </Button>
      }
    >
      <Typography.Paragraph type="secondary" style={{ margin: '0 4px 10px', fontSize: 12 }}>
        提供供用户购买的积分包，适用于需要额外补充积分的用户。与套餐（Plan）相互独立。
      </Typography.Paragraph>
      <Table
        className="pf-data-table"
        size="small"
        rowKey="id"
        loading={loading}
        dataSource={packages}
        pagination={false}
        columns={[
          { title: '名称', dataIndex: 'name' },
          { title: '币种', dataIndex: 'currency', width: 70 },
          { title: '售价', width: 100, render: (_: unknown, row: TopUpPackage) => formatMoneyMinor(row.amount_minor, row.currency) },
          { title: '基础积分', width: 100, render: (_: unknown, row: TopUpPackage) => formatCredits(row.base_credits) },
          { title: '赠送积分', width: 100, render: (_: unknown, row: TopUpPackage) => formatCredits(row.bonus_credits) },
          { title: '总积分', width: 100, render: (_: unknown, row: TopUpPackage) => formatCredits(row.total_credits) },
          {
            title: '等效价格 / 积分',
            width: 120,
            render: (_: unknown, row: TopUpPackage) => formatPricePerCredit(row.amount_minor, row.currency, Number(row.total_credits)),
          },
          {
            title: '状态',
            dataIndex: 'enabled',
            width: 90,
            render: (v: boolean, row) => <Switch size="small" checked={v} onChange={(checked) => toggleEnabled(row, checked)} />,
          },
          {
            title: '操作',
            width: 120,
            render: (_: unknown, row: TopUpPackage) => (
              <Space size={4}>
                <Button size="small" type="link" onClick={() => setEditing(row)}>
                  编辑
                </Button>
                <Button size="small" type="link" danger onClick={() => void archive(row)}>
                  停用
                </Button>
              </Space>
            ),
          },
        ]}
      />
      <TopUpPackageDrawer
        open={addOpen || Boolean(editing)}
        editing={editing}
        onClose={() => {
          setAddOpen(false)
          setEditing(null)
        }}
        onCreated={(pkg) => onChanged([...packages, pkg])}
        onUpdated={(pkg) => onChanged(packages.map((p) => (p.id === pkg.id ? pkg : p)))}
      />
    </SectionCard>
  )
}

function TopUpPackageDrawer({
  open,
  editing,
  onClose,
  onCreated,
  onUpdated,
}: {
  open: boolean
  editing: TopUpPackage | null
  onClose: () => void
  onCreated: (p: TopUpPackage) => void
  onUpdated: (p: TopUpPackage) => void
}) {
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    if (editing) {
      form.setFieldsValue({
        name: editing.name,
        currency: editing.currency,
        amount: minorToMajorNumber(editing.amount_minor, editing.currency),
        base_credits: Number(editing.base_credits),
        bonus_credits: Number(editing.bonus_credits),
        validity_days: editing.validity_days,
        sort_order: editing.sort_order,
      })
    } else {
      form.resetFields()
      form.setFieldsValue({ currency: 'CNY', bonus_credits: 0, sort_order: 0 })
    }
  }, [open, editing, form])

  const submit = async () => {
    const values = await form.validateFields()
    setSubmitting(true)
    try {
      if (editing) {
        const res = await platformUpdateTopupPackage(editing.id, {
          name: values.name,
          amount_minor: majorNumberToMinor(values.amount, editing.currency),
          base_credits: values.base_credits,
          bonus_credits: values.bonus_credits,
          validity_days: values.validity_days ?? null,
          sort_order: values.sort_order,
        })
        onUpdated(res.data)
        message.success('充值包已更新')
      } else {
        const res = await platformCreateTopupPackage({
          name: values.name,
          currency: values.currency,
          amount_minor: majorNumberToMinor(values.amount, values.currency),
          base_credits: values.base_credits,
          bonus_credits: values.bonus_credits ?? 0,
          validity_days: values.validity_days ?? null,
          sort_order: values.sort_order ?? 0,
        })
        onCreated(res.data)
        message.success('充值包已创建')
      }
      onClose()
    } catch (err) {
      message.error(errMsg(err, '保存失败'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Drawer title={editing ? '编辑充值包' : '新增充值包'} open={open} onClose={onClose} width={420} destroyOnClose>
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="名称" rules={[{ required: true }]}>
          <Input placeholder="例如：中额充值包" />
        </Form.Item>
        <Form.Item name="currency" label="币种" rules={[{ required: true }]}>
          <Select disabled={Boolean(editing)} options={ACTIVE_CURRENCIES.map((c) => ({ label: c, value: c }))} />
        </Form.Item>
        <Form.Item name="amount" label="充值金额" rules={[{ required: true }]}>
          <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="base_credits" label="基础积分" rules={[{ required: true }]}>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="bonus_credits" label="赠送积分">
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="validity_days" label="有效期（天，留空为永久）">
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="sort_order" label="排序">
          <InputNumber style={{ width: '100%' }} />
        </Form.Item>
      </Form>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button onClick={onClose}>取消</Button>
        <Button type="primary" loading={submitting} onClick={() => void submit()}>
          {editing ? '保存' : '创建'}
        </Button>
      </div>
    </Drawer>
  )
}

function CustomTopUpCard({
  policies,
  onChanged,
}: {
  policies: CustomTopUpPolicy[]
  loading: boolean
  onChanged: (p: CustomTopUpPolicy[]) => void
}) {
  const [drafts, setDrafts] = useState<Record<string, CustomTopUpPolicy>>({})
  const [saving, setSaving] = useState<string | null>(null)
  const [quoteCurrency, setQuoteCurrency] = useState<string>('CNY')
  const [quoteAmount, setQuoteAmount] = useState<number | null>(null)
  const [quote, setQuote] = useState<CustomTopUpQuote | null>(null)
  const [quoting, setQuoting] = useState(false)

  const byCurrency = useMemo(() => {
    const map = new Map<string, CustomTopUpPolicy>()
    for (const p of policies) map.set(p.currency, p)
    return map
  }, [policies])

  const rowFor = (currency: string): CustomTopUpPolicy =>
    drafts[currency] ??
    byCurrency.get(currency) ?? {
      currency,
      enabled: false,
      min_amount_minor: 0,
      max_amount_minor: 0,
      credits_per_major_unit: '0',
      daily_max_topup_count: null,
      daily_max_topup_amount_minor: null,
      updated_at: '',
      updated_by: null,
    }

  const setDraft = (currency: string, patch: Partial<CustomTopUpPolicy>) => {
    setDrafts((d) => ({ ...d, [currency]: { ...rowFor(currency), ...patch } }))
  }

  const save = async (currency: string) => {
    const row = rowFor(currency)
    setSaving(currency)
    try {
      const res = await platformUpsertCustomTopupPolicy(currency, {
        enabled: row.enabled,
        min_amount_minor: majorNumberToMinor(minorToMajorNumber(row.min_amount_minor, currency), currency),
        max_amount_minor: majorNumberToMinor(minorToMajorNumber(row.max_amount_minor, currency), currency),
        credits_per_major_unit: row.credits_per_major_unit,
        daily_max_topup_count: row.daily_max_topup_count,
        daily_max_topup_amount_minor: row.daily_max_topup_amount_minor,
      })
      onChanged([...policies.filter((p) => p.currency !== currency), res.data])
      setDrafts((d) => {
        const next = { ...d }
        delete next[currency]
        return next
      })
      message.success(`${currency} 自定义充值策略已保存`)
    } catch (err) {
      message.error(errMsg(err, '保存失败'))
    } finally {
      setSaving(null)
    }
  }

  const runQuote = async () => {
    if (quoteAmount === null) return
    setQuoting(true)
    try {
      const res = await platformQuoteCustomTopup({ currency: quoteCurrency, amount_minor: majorNumberToMinor(quoteAmount, quoteCurrency) })
      setQuote(res.data)
    } catch (err) {
      message.error(errMsg(err, '试算失败'))
    } finally {
      setQuoting(false)
    }
  }

  return (
    <SectionCard title="自定义充值（Custom Top-Up）">
      <Typography.Paragraph type="secondary" style={{ margin: '0 4px 10px', fontSize: 12 }}>
        允许用户输入自定义金额充值，兑换比例按币种独立配置（例如 CNY: 1 = 50 积分，USD: 1 = 350 积分），不使用单一全局汇率。
      </Typography.Paragraph>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: '2 1 420px' }}>
          {ACTIVE_CURRENCIES.map((currency) => {
            const row = rowFor(currency)
            return (
              <div key={currency} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '6px 4px' }}>
                <Tag>{currency}</Tag>
                <Switch size="small" checked={row.enabled} onChange={(checked) => setDraft(currency, { enabled: checked })} />
                <span style={{ fontSize: 12 }}>最低</span>
                <InputNumber
                  size="small"
                  min={0}
                  value={minorToMajorNumber(row.min_amount_minor, currency)}
                  onChange={(v) => setDraft(currency, { min_amount_minor: majorNumberToMinor(v ?? 0, currency) })}
                  style={{ width: 90 }}
                />
                <span style={{ fontSize: 12 }}>最高</span>
                <InputNumber
                  size="small"
                  min={0}
                  value={minorToMajorNumber(row.max_amount_minor, currency)}
                  onChange={(v) => setDraft(currency, { max_amount_minor: majorNumberToMinor(v ?? 0, currency) })}
                  style={{ width: 100 }}
                />
                <span style={{ fontSize: 12 }}>兑换比例 1 {currency} =</span>
                <InputNumber
                  size="small"
                  min={0}
                  value={Number(row.credits_per_major_unit)}
                  onChange={(v) => setDraft(currency, { credits_per_major_unit: String(v ?? 0) })}
                  style={{ width: 90 }}
                />
                <span style={{ fontSize: 12 }}>积分</span>
                <Button size="small" type="primary" loading={saving === currency} onClick={() => void save(currency)}>
                  保存
                </Button>
              </div>
            )
          })}
        </div>
        <div style={{ flex: '1 1 240px', minWidth: 220 }}>
          <SectionCard title="自定义充值试算">
            <Space direction="vertical" style={{ width: '100%', padding: 6 }}>
              <Select value={quoteCurrency} onChange={setQuoteCurrency} options={ACTIVE_CURRENCIES.map((c) => ({ label: c, value: c }))} style={{ width: '100%' }} />
              <InputNumber placeholder="充值金额" min={0} value={quoteAmount} onChange={setQuoteAmount} style={{ width: '100%' }} />
              <Button loading={quoting} onClick={() => void runQuote()} block>
                试算
              </Button>
              {quote && (
                <div style={{ fontSize: 13, marginTop: 6 }}>
                  <div>基础积分：{formatCredits(quote.base_credits)}</div>
                  <div>赠送积分：{formatCredits(quote.bonus_credits)}</div>
                  <div style={{ fontWeight: 600 }}>总积分：{formatCredits(quote.total_credits)}</div>
                </div>
              )}
            </Space>
          </SectionCard>
        </div>
      </div>
    </SectionCard>
  )
}

function TopUpBonusRulesCard({
  rules,
  loading,
  onChanged,
}: {
  rules: TopUpBonusRule[]
  loading: boolean
  onChanged: (r: TopUpBonusRule[]) => void
}) {
  const [open, setOpen] = useState(false)

  const toggle = async (row: TopUpBonusRule, enabled: boolean) => {
    try {
      const res = await platformUpdateTopupBonusRule(row.id, { enabled })
      onChanged(rules.map((r) => (r.id === row.id ? res.data : r)))
    } catch (err) {
      message.error(errMsg(err, '更新失败'))
    }
  }

  const remove = async (row: TopUpBonusRule) => {
    try {
      await platformDeleteTopupBonusRule(row.id)
      onChanged(rules.filter((r) => r.id !== row.id))
      message.success('已删除')
    } catch (err) {
      message.error(errMsg(err, '删除失败'))
    }
  }

  return (
    <SectionCard
      title="充值赠送规则（Top-Up Bonus Rule）"
      extra={
        <Button type="primary" size="small" onClick={() => setOpen(true)}>
          + 新增规则
        </Button>
      }
    >
      <Typography.Paragraph type="secondary" style={{ margin: '0 4px 10px', fontSize: 12 }}>
        例如「充值达到 ¥500，赠送 10%」。百分比规则最终仍会转换为固定 Credits 发放，不产生「钱余额」和「积分余额」的语义混乱。
      </Typography.Paragraph>
      <Table
        className="pf-data-table"
        size="small"
        rowKey="id"
        loading={loading}
        dataSource={rules}
        pagination={false}
        columns={[
          { title: '币种', dataIndex: 'currency', width: 70 },
          { title: '触发条件', width: 160, render: (_: unknown, row: TopUpBonusRule) => `${row.threshold_type === 'MIN_AMOUNT' ? '满' : '恰好'} ${formatMoneyMinor(row.threshold_amount_minor, row.currency)}` },
          {
            title: '赠送',
            width: 140,
            render: (_: unknown, row: TopUpBonusRule) => (row.bonus_type === 'PERCENTAGE' ? `+${row.bonus_value}%` : `+${formatCredits(row.bonus_value)} 积分`),
          },
          { title: '状态', dataIndex: 'enabled', width: 90, render: (v: boolean, row) => <Switch size="small" checked={v} onChange={(checked) => toggle(row, checked)} /> },
          {
            title: '操作',
            width: 90,
            render: (_: unknown, row: TopUpBonusRule) => (
              <Button size="small" type="link" danger onClick={() => void remove(row)}>
                删除
              </Button>
            ),
          },
        ]}
      />
      <TopUpBonusRuleDrawer open={open} onClose={() => setOpen(false)} onCreated={(r) => onChanged([...rules, r])} />
    </SectionCard>
  )
}

function TopUpBonusRuleDrawer({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated: (r: TopUpBonusRule) => void
}) {
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (open) {
      form.resetFields()
      form.setFieldsValue({ currency: 'CNY', threshold_type: 'MIN_AMOUNT', bonus_type: 'PERCENTAGE' })
    }
  }, [open, form])

  const submit = async () => {
    const values = await form.validateFields()
    setSubmitting(true)
    try {
      const res = await platformCreateTopupBonusRule({
        currency: values.currency,
        threshold_type: values.threshold_type,
        threshold_amount_minor: majorNumberToMinor(values.threshold_amount, values.currency),
        bonus_type: values.bonus_type,
        bonus_value: values.bonus_value,
      })
      onCreated(res.data)
      message.success('规则已创建')
      onClose()
    } catch (err) {
      message.error(errMsg(err, '创建失败'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Drawer title="新增充值赠送规则" open={open} onClose={onClose} width={420} destroyOnClose>
      <Form form={form} layout="vertical">
        <Form.Item name="currency" label="币种" rules={[{ required: true }]}>
          <Select options={ACTIVE_CURRENCIES.map((c) => ({ label: c, value: c }))} />
        </Form.Item>
        <Form.Item name="threshold_type" label="触发方式" rules={[{ required: true }]}>
          <Select
            options={[
              { label: '达到金额（MIN_AMOUNT）', value: 'MIN_AMOUNT' },
              { label: '固定金额（FIXED_AMOUNT）', value: 'FIXED_AMOUNT' },
            ]}
          />
        </Form.Item>
        <Form.Item name="threshold_amount" label="触发金额" rules={[{ required: true }]}>
          <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="bonus_type" label="赠送方式" rules={[{ required: true }]}>
          <Select
            options={[
              { label: '按百分比（PERCENTAGE）', value: 'PERCENTAGE' },
              { label: '固定积分（FIXED_CREDITS）', value: 'FIXED_CREDITS' },
            ]}
          />
        </Form.Item>
        <Form.Item name="bonus_value" label="赠送数值" extra="百分比填 10 代表 +10%；固定积分直接填 Credits 数量" rules={[{ required: true }]}>
          <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
        </Form.Item>
      </Form>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button onClick={onClose}>取消</Button>
        <Button type="primary" loading={submitting} onClick={() => void submit()}>
          创建
        </Button>
      </div>
    </Drawer>
  )
}
